import styled from 'styled-components';

import { spacing } from '@savgroup-front-common/primitive/helpers/spacing';
import { DefaultStyledProps } from '@savgroup-front-common/types';

import { fontStyle } from '../../../../theme/helpers/fontStyle';

import {
  colorBasedOnProps,
  colorHoverBasedOnProps,
} from './RadioButton.helpers';

interface RadioInputProps extends DefaultStyledProps {
  danger?: boolean;
  isHighlighted: boolean;
  $isChecked?: boolean;
  $isRequired?: boolean;
  disabled?: boolean;
}
export const $RadioInput = styled.input<RadioInputProps>`
  position: absolute;
  z-index: -1;
  top: 0;
  left: 0;
  width: ${spacing('xl')};
  height: ${spacing('xl')};
  opacity: 0;

  & {
    ~ span {
      transition:
        color 100ms ease-in-out,
        border-color 100ms ease-in-out;

      border-color: ${(props) => {
        const { theme, isHighlighted, $isChecked } = props;

        if ($isChecked) {
          return colorHoverBasedOnProps(props);
        }

        if (isHighlighted) {
          return theme.colors.primary;
        }

        return null;
      }};
      color: ${(props) => {
        const { theme, isHighlighted, $isChecked } = props;

        if (isHighlighted) {
          return theme.colors.primary;
        }
        if ($isChecked) {
          return theme.newUI.defaultColors.text;
        }

        return null;
      }};
    }
  }

  &:hover {
    ~ span {
      border-color: ${(props) =>
        props.disabled || props.danger ? null : colorHoverBasedOnProps(props)};
    }
  }

  &:checked {
    ~ span {
      color: ${({ theme }) => theme.newUI.defaultColors.text};

      &::before {
        transform: scale(1);
        transform-origin: 50% 50%;
        background-color: ${(props) => colorHoverBasedOnProps(props)};
      }
    }
  }
`;

interface RadioSpanProps extends DefaultStyledProps {
  disabled?: boolean;
  $isChecked?: boolean;
  $hasDescription?: boolean;
  $isCompact?: boolean;
}
export const $RadioSpan = styled.span<RadioSpanProps>`
  display: block;
  box-sizing: content-box;
  width: ${spacing('lg')};
  height: ${spacing('lg')};
  border-radius: 50%;
  border-width: ${({ theme }) => theme.borders.thicknessSpecialInput};
  border-color: ${(props) => colorBasedOnProps(props)};
  border-style: solid;
  margin: ${({ $isCompact }) => {
    if ($isCompact) {
      return '0';
    }

    return '16px';
  }};

  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  transition-duration: 0.2s;

  &::before {
    content: '';
    display: block;
    position: relative;
    border-radius: 50%;
    top: ${spacing('xxs')};
    left: ${spacing('xxs')};
    width: ${spacing('md')};
    height: ${spacing('md')};
    background-color: ${(props) =>
      props.$isChecked
        ? colorHoverBasedOnProps(props)
        : colorBasedOnProps(props)};
    transform: scale(
      ${({ disabled, $isChecked }) => (disabled || $isChecked ? 1 : 0)}
    );
    transform-origin: 50% 50%;
    transition-duration: 0.2s;
  }
`;

interface LockedSpanProps extends DefaultStyledProps {
  disabled?: boolean;
  $hasDescription?: boolean;
  $isCompact?: boolean;
}
export const $LockedSpan = styled.span<LockedSpanProps>`
  display: block;
  box-sizing: content-box;
  margin: ${({ $isCompact }) => {
    if ($isCompact) {
      return spacing(0);
    }

    return spacing('lg');
  }};
  color: ${({ theme }) => theme.colors.paragraphTextColor};

  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
`;

interface LabelProps extends DefaultStyledProps {
  $isFluid?: boolean;
  $isChecked?: boolean;
  disabled?: boolean;
  isRequired?: boolean;
  $isCompact: boolean;
}
export const $RadioLabel = styled.label<LabelProps>`
  display: block;
  position: relative;
  padding: 0;
  padding-left: 0;
  font-weight: ${({ $isChecked }) => ($isChecked ? 'bold' : 'normal')};
  width: ${({ $isFluid }) => ($isFluid ? '100%' : 'fit-content')};
  min-height: ${spacing(7)};

  margin: 0 0 ${spacing('md')} 0;
  color: ${({ theme, disabled }) => {
    if (disabled) {
      return theme.colors.paragraphTextColor;
    }

    return theme.colors.mainTextColor;
  }};
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};

  border: ${({ theme, $isCompact }) => {
    if ($isCompact) {
      return null;
    }

    return `1px solid ${theme.newUI.variationColors?.light4}`;
  }};
  border-radius: ${({ theme }) => theme.newUI.borders.bigRadius};

  background-color: ${({ theme, $isCompact }) => {
    if ($isCompact) {
      return null;
    }

    return theme.systemColors.secondaryBackground;
  }};

  box-shadow: ${({ theme, $isCompact }) => {
    if ($isCompact) {
      return null;
    }

    return theme.newUI.shadows.level1;
  }};
  &:first-child {
    margin-left: 0;
  }

  &:last-child {
    margin: 0;
  }
  transition-duration: 0.2s;

  &:hover {
    border-color: ${({ theme }) => theme.newUI.variationColors?.dark1};
  }
`;

export const $RadioDescription = styled.div`
  color: ${({ theme }) => theme.colors.paragraphTextColor};
  ${fontStyle('xs')};
  margin-top: calc(-1 * ${spacing('xxs')});
`;

export const $Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${spacing('lg')};

  & > *:not(:first-child) {
    flex: 1;
  }

  & .tooltip-trigger {
    display: flex;
  }
`;
